import { isMatch, useMatches } from '@tanstack/react-router';
import { cn } from '@tempo-io/sui-class-utils';
import { Fragment } from 'react';
import { HeaderBreadcrumbs } from './HeaderBreadcrumbs';
import { usePageActions } from './PageActionsContext';

export const Title = ({ title }: { title?: string | React.ReactNode }) => {
  if (!title) return null;

  return (
    <span>
      <h2 data-testid="page-header-title" className="text-2xl font-semibold">
        {title}
      </h2>
    </span>
  );
};

const Description = ({ description }: { description?: string }) => (
  <p data-testid="page-header-description" className="text-xs text-muted-foreground">
    {description}
  </p>
);

interface PageHeaderProps {
  // Optional overrides for route-based data
  title?: string | React.ReactNode;
  description?: string;
  customBreadcrumbs?: React.ReactNode;
  disableBreadcrumbs?: boolean;
  actions?: React.ReactNode;
  className?: string;
}

export const PageHeader = (props: PageHeaderProps = {}) => {
  const matches = useMatches();
  const currentMatch = matches[matches.length - 1];
  const { actions: dynamicActions } = usePageActions();

  const dynamicTitle = matches
    .slice()
    .reverse()
    .find((match) => isMatch(match, 'loaderData.dynamicTitle'))?.loaderData?.dynamicTitle;

  // Use props to override route data if provided
  const pageTitle = props.title || dynamicTitle || currentMatch?.staticData?.topNav?.title;
  const pageDescription = props.description || currentMatch?.staticData?.topNav?.description;

  // Check if we're on a main section page (hide breadcrumbs on main pages)
  const matchesWithCrumbs = matches.filter((match) => isMatch(match, 'loaderData.crumb'));
  const isMainPage = matchesWithCrumbs.length <= 2; // Main pages have org + section breadcrumbs only
  // Check if we're on a subpage (like backlog, scenario, etc.) - show breadcrumbs even with 2 crumbs
  const pathname = currentMatch?.pathname || '';
  const isSubpage = pathname.includes('/backlog') || pathname.includes('/scenario');

  // When custom breadcrumbs (Insights) are provided, always show them (don't apply route-based hiding logic)
  const shouldHideBreadcrumbs =
    props.disableBreadcrumbs || (isMainPage && !isSubpage && !props.customBreadcrumbs);

  return (
    <div className={cn('sticky top-0 z-50 w-full bg-background', props.className)}>
      <div className="flex flex-col gap-2 px-6 py-4">
        {!shouldHideBreadcrumbs && (
          <HeaderBreadcrumbs customBreadcrumbs={props.customBreadcrumbs} />
        )}
        <div className="flex flex-col">
          <div className="flex items-center justify-between">
            <Title title={pageTitle} />
            {(dynamicActions.length > 0 || props.actions) && (
              <div className="flex items-center gap-2">
                {dynamicActions.map((renderFn, index) => (
                  <Fragment key={index}>{renderFn()}</Fragment>
                ))}
                {props.actions}
              </div>
            )}
          </div>
          {pageDescription && <Description description={pageDescription} />}
        </div>
      </div>
    </div>
  );
};
